/**
 * P5 of dashboard MFA (backend aidocs/plans/2026-10-20-dashboard-password-mfa.md §3.8; apiv2/dashboard_mfa.openapi.yaml
 * dashboardUserResetMfa): a tenant admin resets a colleague's two-step sign-in from the Users grid. The backend
 * removes their authenticator apps, phone numbers and recovery codes and ends their dashboard sessions; it refuses
 * oneself (`cannot_reset_self`), an admin without the explicit permission (`permission_required`) and an admin whose
 * own session did not pass two-step sign-in (`session_mfa_required`), in its own words.
 *
 * Gated on the explicit-only `user.mfa_reset` (hasExplicitPerm): Full access does not imply it, like
 * notification.mcp_*, so hasPerm would show the action to full-access admins and then refuse them.
 */
import LockResetIcon from '@mui/icons-material/LockReset';
import { Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import React from 'react';

import { resetColleagueMfa } from '../account/securityApi';

export const RESET_MFA_LABEL = 'Reset two-step sign-in';

/** Confirms, then resets. Resolves true when it was reset. */
export async function resetTwoStepSignIn({ dataAPI, user, confirm, enqueueSnackbar }) {
    const name = user.email || user.display_name || `user ${user.id}`;
    const ok = await confirm({
        title: 'Reset two-step sign-in?',
        body: `${name}'s authenticator app, phone number and recovery codes are removed, and their dashboard sessions end. `
            + 'They sign in again with their password, and set up two-step sign-in again if your company requires it.',
        okButtonText: 'Reset',
        destructive: true,
    });
    if (!ok) {
        return false;
    }
    try {
        await resetColleagueMfa(dataAPI, user.id);
        enqueueSnackbar(`Two-step sign-in reset for ${name}.`, { variant: 'success' });
        return true;
    } catch (err) {
        enqueueSnackbar(err.status === 404 && !err.code ? 'That user was not found.' : err.message, { variant: 'error' });
        return false;
    }
}

/** The grid row action, or null without the permission. */
export function resetMfaAction({ row, hasExplicitPerm, onReset }) {
    if (!hasExplicitPerm || !hasExplicitPerm('user', 'mfa_reset')) {
        return null;
    }
    return (
        <GridActionsCellItem
            key="reset-mfa"
            icon={<Tooltip title={RESET_MFA_LABEL}><LockResetIcon color="primary" /></Tooltip>}
            label={RESET_MFA_LABEL}
            onClick={() => onReset(row)}
        />
    );
}
